'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  ArrowUpDown,
  Users,
  Building2,
  Clock,
  BookUser,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ModalFilterBar } from '@/components/CapacitorModalFilterHeader';

export type CallsContactsSort = 'name_asc' | 'name_desc' | 'online_first';
export type CallsRecentsScope = 'all' | 'sip' | 'team';
export type CallsRecentsTimeSort = 'newest' | 'oldest';

type CallsListTab = 'contacts' | 'recents';

export interface TotalConnectCallsListToolbarProps {
  activeTab: CallsListTab;
  isDark: boolean;
  primaryColor: string;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  /** When true, Contacts tab scope dropdown includes CRM (requires assigned SIP extension with outbound). */
  allowCrmContacts: boolean;
  contactsSubTab: 'team' | 'crm';
  onContactsSubTabChange: (t: 'team' | 'crm') => void;
  contactsSort: CallsContactsSort;
  onContactsSortChange: (s: CallsContactsSort) => void;
  recentsScope: CallsRecentsScope;
  onRecentsScopeChange: (s: CallsRecentsScope) => void;
  recentsTimeSort: CallsRecentsTimeSort;
  onRecentsTimeSortChange: (s: CallsRecentsTimeSort) => void;
  canAccessPhoneContacts: boolean;
  onOpenPhoneContacts?: () => void;
}

/**
 * List chrome for Total Connect calls: scope dropdown + collapsible search +
 * sort, composed on the shared ModalFilterBar used by SmartTask list steps.
 */
export function TotalConnectCallsListToolbar({
  activeTab,
  isDark,
  searchQuery,
  onSearchChange,
  allowCrmContacts,
  contactsSubTab,
  onContactsSubTabChange,
  contactsSort,
  onContactsSortChange,
  recentsScope,
  onRecentsScopeChange,
  recentsTimeSort,
  onRecentsTimeSortChange,
  canAccessPhoneContacts,
  onOpenPhoneContacts,
}: TotalConnectCallsListToolbarProps) {
  const [showScopeDropdown, setShowScopeDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const scopeRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (scopeRef.current && !scopeRef.current.contains(e.target as Node)) {
        setShowScopeDropdown(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const scopeLabel =
    activeTab === 'recents'
      ? recentsScope === 'all'
        ? 'All calls'
        : recentsScope === 'sip'
          ? 'SIP / PSTN'
          : 'Team (app)'
      : contactsSubTab === 'team'
        ? 'Team'
        : 'CRM';

  const ScopeIcon =
    activeTab === 'recents' ? Clock : contactsSubTab === 'team' ? Users : Building2;

  const searchPlaceholder =
    activeTab === 'recents'
      ? 'Search call history...'
      : contactsSubTab === 'crm'
        ? 'Search CRM...'
        : 'Search team members...';

  const showPhoneBook =
    activeTab === 'contacts' &&
    contactsSubTab === 'team' &&
    canAccessPhoneContacts &&
    onOpenPhoneContacts;

  const sortOptions: { key: string; label: string; active: boolean; onSelect: () => void }[] =
    activeTab === 'recents'
      ? [
          {
            key: 'newest',
            label: 'Newest first',
            active: recentsTimeSort === 'newest',
            onSelect: () => onRecentsTimeSortChange('newest'),
          },
          {
            key: 'oldest',
            label: 'Oldest first',
            active: recentsTimeSort === 'oldest',
            onSelect: () => onRecentsTimeSortChange('oldest'),
          },
        ]
      : contactsSubTab === 'crm'
        ? [
            {
              key: 'name_asc',
              label: 'Name A–Z',
              active: contactsSort === 'name_asc',
              onSelect: () => onContactsSortChange('name_asc'),
            },
            {
              key: 'name_desc',
              label: 'Name Z–A',
              active: contactsSort === 'name_desc',
              onSelect: () => onContactsSortChange('name_desc'),
            },
          ]
        : [
            {
              key: 'name_asc',
              label: 'Name A–Z',
              active: contactsSort === 'name_asc',
              onSelect: () => onContactsSortChange('name_asc'),
            },
            {
              key: 'name_desc',
              label: 'Name Z–A',
              active: contactsSort === 'name_desc',
              onSelect: () => onContactsSortChange('name_desc'),
            },
            {
              key: 'online_first',
              label: 'Online first',
              active: contactsSort === 'online_first',
              onSelect: () => onContactsSortChange('online_first'),
            },
          ];

  return (
    <ModalFilterBar
      isDark={isDark}
      search={{ value: searchQuery, onChange: onSearchChange, placeholder: searchPlaceholder }}
      extraActions={
        <>
          {showPhoneBook ? (
            <button
              type="button"
              onClick={onOpenPhoneContacts}
              className={cn(
                'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border shadow-sm transition-colors',
                isDark
                  ? 'border-slate-700/70 bg-slate-800/80 text-slate-300 hover:bg-slate-700/80'
                  : 'border-slate-200/80 bg-white/80 text-slate-500 hover:bg-slate-100/80',
              )}
              title="Device contacts"
              aria-label="Device contacts"
            >
              <BookUser className="h-4 w-4" />
            </button>
          ) : null}

          <div ref={sortRef} className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                setShowScopeDropdown(false);
                setShowSortDropdown((v) => !v);
              }}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm transition-colors',
                showSortDropdown
                  ? 'border-blue-300 bg-blue-100 text-blue-600 dark:border-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                  : isDark
                    ? 'border-slate-700/70 bg-slate-800/80 text-slate-300 hover:bg-slate-700/80'
                    : 'border-slate-200/80 bg-white/80 text-slate-500 hover:bg-slate-100/80',
              )}
              title="Sort"
              aria-label="Sort"
            >
              <ArrowUpDown className="h-4 w-4" />
            </button>
            {showSortDropdown ? (
              <div
                className={cn(
                  'absolute right-0 top-full z-[200] mt-1 w-48 rounded-lg border shadow-lg',
                  isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white',
                )}
              >
                <div
                  className={cn(
                    'border-b px-2 py-2',
                    isDark ? 'border-gray-700' : 'border-gray-200',
                  )}
                >
                  <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Sort
                  </span>
                </div>
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      opt.onSelect();
                      setShowSortDropdown(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700',
                      opt.active &&
                        'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </>
      }
    >
      {/* Scope dropdown — flex-1 so it fills the space between search and actions */}
      <div ref={scopeRef} className="relative min-w-0 flex-1">
        <button
          type="button"
          onClick={() => {
            setShowSortDropdown(false);
            setShowScopeDropdown((v) => !v);
          }}
          className={cn(
            'flex w-auto max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition-all',
            showScopeDropdown
              ? 'border-gray-400 bg-gray-100 dark:border-gray-500 dark:bg-gray-700'
              : isDark
                ? 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700'
                : 'border-gray-200 bg-white/95 text-gray-700 hover:bg-gray-50',
          )}
        >
          <ScopeIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <span className="min-w-0 truncate">{scopeLabel}</span>
          <ChevronDown
            className={cn(
              'h-3 w-3 flex-shrink-0 transition-transform',
              showScopeDropdown && 'rotate-180',
            )}
          />
        </button>

        {showScopeDropdown && (
          <div
            className={cn(
              'absolute left-0 top-full z-[200] mt-1 w-48 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border py-1 shadow-2xl',
              isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white',
            )}
          >
            {activeTab === 'recents' ? (
              (['all', 'sip', 'team'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    onRecentsScopeChange(key);
                    setShowScopeDropdown(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium transition-colors',
                    recentsScope === key
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : isDark
                        ? 'text-gray-300 hover:bg-gray-700/50'
                        : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {key === 'all' ? 'All calls' : key === 'sip' ? 'SIP / PSTN' : 'Team (app)'}
                </button>
              ))
            ) : allowCrmContacts ? (
              (['team', 'crm'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    onContactsSubTabChange(key);
                    setShowScopeDropdown(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium transition-colors',
                    contactsSubTab === key
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : isDark
                        ? 'text-gray-300 hover:bg-gray-700/50'
                        : 'text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {key === 'team' ? 'Team' : 'CRM'}
                </button>
              ))
            ) : (
              <div
                className={cn(
                  'px-3 py-2 text-sm',
                  isDark ? 'text-gray-400' : 'text-gray-500',
                )}
              >
                Team contacts
              </div>
            )}
          </div>
        )}
      </div>
    </ModalFilterBar>
  );
}
